import { useEffect, useMemo, useRef, useState } from 'react';
import { useComments } from '../../hooks/useComments';
import { CommentBubble, type BubbleActor } from './CommentBubble';
import { useCommentOrder } from './CommentOrderToggle';

/**
 * Phase 25 Plan 25-05 — comment thread container.
 *
 * Fetches via useComments(taskId) — server returns oldest-first per D-11; the
 * container does NOT re-sort, it trusts the API order. The list has no
 * scrollbox of its own (MKS-145) — it flows inside the slide-over body, which
 * owns the single scrollbar. Auto-scrolls to the bottom only when the count
 * GROWS after initial load (post-create): scrolling on initial load would now
 * yank the whole panel past the title/description. The `lastCountRef`
 * comparison also prevents repeat scrolls on stale-cache rerenders where the
 * array identity changes but the length doesn't.
 */
/**
 * MKS-384 — which comment to scroll to and flash. `nonce` is what makes a
 * REPEAT jump to the same comment work: the effect fires per nonce, not per id,
 * so clicking the same "N questions waiting" entry twice scrolls twice.
 */
export interface CommentHighlight {
  commentId: number;
  nonce: number;
}

/** MKS-384 — the DOM id a `?comment=<id>` deep link and the marker both aim at. */
export function commentAnchorId(commentId: number): string {
  return `task-comment-${commentId}`;
}

/** How long a jumped-to comment stays flashed. */
const HIGHLIGHT_MS = 3000;

export interface CommentListProps {
  taskId: number;
  actor: BubbleActor;
  /**
   * MKS-384 — set by the `?comment=<id>` deep link out of a Slack DM and by the
   * task detail's pending-question marker. Null (and the whole flag-off branch)
   * leaves the list exactly as it was: anchors only, no scroll, no highlight.
   */
  highlight?: CommentHighlight | null;
  /**
   * MKS-514 — when true, the list follows the user's `commentOrder`
   * preference (`desc` = newest on top). False (default, flag off, portal)
   * keeps the server's oldest-first order.
   */
  orderable?: boolean;
}

export function CommentList({
  taskId,
  actor,
  highlight = null,
  orderable = false,
}: CommentListProps) {
  const { data, isLoading, isError, refetch } = useComments(taskId);
  const order = useCommentOrder(orderable);
  const comments = useMemo(
    () => (data && order === 'desc' ? [...data].reverse() : data),
    [data, order],
  );
  const topRef = useRef<HTMLDivElement | null>(null); // MKS-514
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const lastCountRef = useRef<number | null>(null);
  const itemRefs = useRef(new Map<number, HTMLDivElement>());
  const [flashedId, setFlashedId] = useState<number | null>(null);
  // The nonce already handled, so a rerender on a stale-cache refresh does not
  // yank the panel back to the same comment again.
  const handledNonceRef = useRef<number | null>(null);

  // Auto-scroll to bottom only when the count grows after initial load —
  // the scrollable ancestor is the whole slide-over body now (MKS-145).
  useEffect(() => {
    if (!data) return;
    if (lastCountRef.current === null) {
      lastCountRef.current = data.length;
      return;
    }
    if (data.length !== lastCountRef.current) {
      lastCountRef.current = data.length;
      // Defer to the next paint so the new bubble is in the DOM. MKS-514 —
      // newest-first puts the new bubble on top, so scroll there instead.
      window.requestAnimationFrame(() => {
        if (order === 'desc') {
          topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      });
    }
  }, [data, order]);

  // MKS-384 — scroll the named comment into view once the list is in the DOM,
  // and flash it so the eye lands on it. Deliberately AFTER the auto-scroll
  // effect above: an explicit jump is the user's own intent and wins.
  useEffect(() => {
    if (!data || highlight === null) return;
    if (handledNonceRef.current === highlight.nonce) return;
    const el = itemRefs.current.get(highlight.commentId);
    if (!el) return; // not on this page of the thread — nothing to scroll to
    handledNonceRef.current = highlight.nonce;
    setFlashedId(highlight.commentId);
    // Defer to the next paint, like the auto-scroll above.
    window.requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const timer = window.setTimeout(() => setFlashedId(null), HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [data, highlight]);

  if (isLoading) {
    return <p className="text-sm text-gray-500 italic">Loading comments…</p>;
  }
  if (isError) {
    return (
      <div className="text-sm text-gray-500">
        Couldn’t load comments.
        <button
          type="button"
          onClick={() => refetch()}
          className="ml-2 text-primary-600 hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }
  if (!comments || comments.length === 0) {
    return (
      <p className="text-sm text-gray-400 italic">
        No comments yet — start the thread.
      </p>
    );
  }

  return (
    <div ref={topRef} className="space-y-3">
      {comments.map((c) => (
        // MKS-384 — the stable per-comment anchor a DM link and the
        // pending-question marker aim at. Always rendered: an id on a div is
        // invisible, so it needs no flag of its own.
        <div
          key={c.id}
          id={commentAnchorId(c.id)}
          ref={(el) => {
            if (el) itemRefs.current.set(c.id, el);
            else itemRefs.current.delete(c.id);
          }}
          className={
            flashedId === c.id
              ? 'rounded-md ring-2 ring-amber-400 ring-offset-2 transition-shadow'
              : 'transition-shadow'
          }
        >
          <CommentBubble taskId={taskId} comment={c} actor={actor} />
        </div>
      ))}
      <div ref={bottomRef} aria-hidden />
    </div>
  );
}
